import { useRef, useState } from "react";
import type { GroupSummary, GroupWithMembers, RestoreVersionMode } from "../../types";
import { isGroupLocked } from "../../types";
import { Button } from "../Button";
import { ModalShell } from "../ModalShell";

interface RestoreVersionModalProps {
  /** The older version being brought forward. */
  group: GroupWithMembers;
  /** The highest-numbered version on the Opportunity. */
  latest: GroupSummary;
  onConfirm: (mode: RestoreVersionMode) => Promise<void>;
  onClose: () => void;
  children?: never;
}

interface ModeOption {
  mode: RestoreVersionMode;
  label: string;
  description: string;
  disabledReason: string | null;
}

/**
 * Brings an older version's members, ratings, and multiplier forward, either by
 * overwriting the latest working version or by adding the next version.
 */
export function RestoreVersionModal({ group, latest, onConfirm, onClose }: RestoreVersionModalProps) {
  const latestLocked = isGroupLocked(latest);
  const options: readonly ModeOption[] = [
    {
      mode: "replaceLatest",
      label: `Replace version ${latest.version}`,
      description: `Overwrite version ${latest.version}'s members, ratings, and multiplier with this version's. It keeps its number, underwriter, and status.`,
      disabledReason: latestLocked ? `Version ${latest.version} is read-only, so it cannot be overwritten.` : null,
    },
    {
      mode: "newVersion",
      label: `Create version ${latest.version + 1}`,
      description: "Add a new working version with these members and ratings, assigned to you. Every other version becomes read-only.",
      disabledReason: null,
    },
  ];
  const [mode, setMode] = useState<RestoreVersionMode>(latestLocked ? "newVersion" : "replaceLatest");
  const [isSaving, setIsSaving] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const selected = options.find((option) => option.mode === mode) ?? options[1];

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      await onConfirm(mode);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell
      title={`Use version ${group.version}`}
      description={`Bring version ${group.version} of ${group.name} forward. Choose how.`}
      onClose={onClose}
      initialFocusRef={cancelRef}
      size="md"
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => void handleConfirm()} isLoading={isSaving}>
            {selected.label}
          </Button>
        </>
      }
    >
      <fieldset className="space-y-2">
        <legend className="sr-only">How to use this version</legend>
        {options.map((option) => {
          const isDisabled = option.disabledReason !== null;
          const isChecked = option.mode === mode;
          return (
            <label key={option.mode} className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${isChecked ? "border-brand-green bg-brand-green-05" : "border-gray-200"} ${isDisabled ? "cursor-default opacity-60" : "hover:bg-gray-50"}`}>
              <input type="radio" name="restore-mode" value={option.mode} checked={isChecked} disabled={isDisabled || isSaving} onChange={() => setMode(option.mode)} className="mt-1 h-4 w-4 accent-brand-green" />
              <span className="text-sm">
                <span className="block font-semibold text-gray-800">{option.label}</span>
                <span className="block text-gray-600">{option.description}</span>
                {option.disabledReason && <span className="block text-xs text-gray-500">{option.disabledReason}</span>}
              </span>
            </label>
          );
        })}
      </fieldset>
    </ModalShell>
  );
}
